import React, { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import Spinner from '@xo-union/tk-component-spinner';
import { H1, Body1 } from '@xo-union/tk-ui-typography';
import { useHistory } from 'react-router-dom';
import useShareTracking from '../SharePage/useShareTracking';
import { useGuestMessagingExperiment } from '../../hooks/experiment/useGuestMessagingExperiment';
import styles from './messages.scss';
import MobileSidebarCell from './MobileSidebarCell';
import SequencePreview from './SequencePreview';
import SidebarCell from './SidebarCell';
import WeddingInfoModal from './WeddingInfoModal';
import {
  useGuestMessagingStatusesQuery,
  useMessagingSequencesQuery,
  useWeddingWebsiteQuery,
} from '../../generated/graphql';
import useQueryString from '../../helpers/useQueryString';
import {
  getSaveTheDateEligibility,
  getSequences,
  SAVE_THE_DATE_MEDIA_ID,
  SEQUENCE_TYPE_RSVP_SCHEDULE,
  Sequences,
} from './messagingHubHelper';

type HubSequence = Sequences[0] & { blocked?: boolean };
const Messages: React.FC = () => {
  const { data: sequenceData, loading } = useMessagingSequencesQuery();
  const { data: statusData, loading: statusLoading } = useGuestMessagingStatusesQuery({ errorPolicy: 'all' });
  const { data: gdsData, loading: gdsLoading } = useWeddingWebsiteQuery();
  const [uniqueStatuses, setUniqueStatuses] = useState<Set<string>>(new Set());
  const [analyticsCalled, setAnalyticsCalled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Defaulting to select Share Website seq
  const [currentSequence, setCurrentSequence] = useState<HubSequence>();
  const [targetSequence, setTargetSequence] = useState<HubSequence>();
  const track = useShareTracking();
  const { loading: experimentLoading } = useGuestMessagingExperiment();
  const history = useHistory();
  const websiteState = gdsData?.weddingWebsite.state;
  const sequenceParam = useQueryString().get('sequence');

  useEffect(() => {
    if (statusData?.guestMessagingStatuses?.length) {
      statusData.guestMessagingStatuses.forEach(i => {
        uniqueStatuses.add(i?.sequenceDisplayName || '');
      });
      setUniqueStatuses(new Set(uniqueStatuses));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData]);

  useEffect(() => {
    if (
      statusData?.guestMessagingStatuses &&
      gdsData?.guestWedding &&
      sequenceData?.messagingSequences &&
      !experimentLoading &&
      !analyticsCalled
    ) {
      track('Messaging Hub Displayed', {
        sentMessageTypes: Array.from(uniqueStatuses),
        rsvpDeadlineStatus: gdsData.guestWedding.rsvpMedium,
        numMessageTypes: sequenceData.messagingSequences.length,
      });
      setAnalyticsCalled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData, uniqueStatuses, gdsData, sequenceData, experimentLoading, analyticsCalled]);

  const sequences = useMemo(() => getSequences(sequenceData, gdsData), [sequenceData, gdsData]);

  const handleSequenceChange = (sequence?: HubSequence) => {
    if (sequenceParam) {
      history.replace({ search: '' });
    }
    const { member, wedding } = gdsData || {};
    if (SAVE_THE_DATE_MEDIA_ID === sequence?.mediaId && !getSaveTheDateEligibility(member, wedding)) {
      setShowModal(true);
      setTargetSequence(sequence);
    } else {
      setCurrentSequence(sequence);
    }

    //Tracking when user selects save the date template
    if (SAVE_THE_DATE_MEDIA_ID === sequence?.mediaId) {
      track('Save the Date Interaction', {
        action: 'Save the Date selected',
        messageType: 'Save the date',
        medium: 'email',
      });
    }
  };

  const handleSubmit = useCallback((sequence?: HubSequence | string) => {
    setShowModal(false);

    // We can safely assume sequence is of a HubSequence type because
    // this component can only pass down HubSequence object in the sequence param.
    setCurrentSequence(sequence as HubSequence);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (sequenceParam) {
      const matchedActive = sequences.active.find(s => s.sequenceType === sequenceParam);
      const matchedBlocked = sequences.blocked.find(s => s.sequenceType === sequenceParam);
      const matched = matchedActive ?? (matchedBlocked ? { ...matchedBlocked, blocked: true } : undefined);

      if (matched && currentSequence?.sequenceType !== matched.sequenceType) {
        setCurrentSequence(matched);
      } else if (!matched && !currentSequence) {
        setCurrentSequence(sequences.active[0]);
      }

      return;
    }

    if (!currentSequence) {
      setCurrentSequence(sequences.active[0]);
    }
  }, [currentSequence, sequenceParam, sequences.active, sequences.blocked]);

  return (
    <>
      {(loading || statusLoading || gdsLoading) && !sequenceData ? (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      ) : (
        <>
          {showModal ? (
            <WeddingInfoModal onSubmit={handleSubmit} onClose={closeModal} sequence={targetSequence} />
          ) : null}
          <H1>Choose your message</H1>
          <Body1 className={styles.subHeading}>
            Use the templates below to easily send details and reminders to guests.
          </Body1>
          <div className={styles.seqContainer}>
            <div className={styles.sidebar}>
              {sequences.active.map((s: HubSequence) => {
                return (
                  <Fragment key={s.sequenceType}>
                    <SidebarCell selectedSequence={currentSequence} sequence={s} onClick={handleSequenceChange} />
                    <MobileSidebarCell
                      selectedSequence={currentSequence}
                      sequence={s}
                      websiteState={websiteState}
                      onClick={handleSequenceChange}
                    />
                  </Fragment>
                );
              })}
              {sequences.blocked.length
                ? sequences.blocked.map((s: HubSequence) => (
                    <Fragment key={s.sequenceType}>
                      <SidebarCell
                        selectedSequence={currentSequence}
                        sequence={s}
                        onClick={() => {
                          handleSequenceChange({ ...s, blocked: true });
                        }}
                      />
                      <MobileSidebarCell
                        selectedSequence={currentSequence}
                        sequence={s}
                        websiteState={websiteState}
                        onClick={() => {
                          handleSequenceChange({ ...s, blocked: true });
                        }}
                      />
                    </Fragment>
                  ))
                : null}
            </div>
            {currentSequence?.sequenceType !== SEQUENCE_TYPE_RSVP_SCHEDULE && (
              <SequencePreview
                supportedTypes={currentSequence?.supportedTypes}
                wwsData={gdsData}
                displayName={currentSequence?.displayName}
                sequenceType={currentSequence?.sequenceType}
                blocked={currentSequence?.blocked}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Messages;
